import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { List, RowComponentProps } from 'react-window';

import './form-submissions-table.css';

import Delayed from '../Delayed';
import { DateRange, DateRangeDropdown, DateRangeDropdownHandle } from '../Filters/DateRangeDropdown';
import { EnumSelectDropdown, EnumSelectDropdownHandle } from '../Filters/EnumSelectDropdown';
import { SearchableStringListDropdown, SearchableStringListDropdownHandle } from '../Filters/SearchableStringListDropdown';
import WordSearchInput, { WordSearch, WordSearchInputHandle } from '../Filters/WordSearchInput';
import ResetFilter from '../Icons/ResetFilter';

import { FormStatus, FormStatusLabel } from '../../enums/FormStatus';
import { Sort } from '../../enums/Sort';

import { isIsoDateInRange } from '../../functions/TimeHelpers';

import IFormSubmission from '../../types/Dashboard/FormSubmission';

type Props = {
    formSubmissions: IFormSubmission[];
    isLoading: boolean;
    onFormStatusClick: (entryID: string) => void;
};

export default function FormSubmissionsTable({
    formSubmissions,
    isLoading,
    onFormStatusClick,
}: Props) {
    // Sort by column (constant for now, could be state if multiple sort columns needed)
    const sortBy = 'submittedDate';

    // Filter states
    const [dateFilter, setDateFilter] = useState<DateRange>({ sort: Sort.Ascending });
    const [descriptionFilter, setDescriptionFilter] = useState<string>('');
    const [modifiedFilter, setModifiedFilter] = useState<string | undefined>();
    const [statusFilter, setStatusFilter] = useState<string>('');

    // Filtered/sorted list
    const [filteredList, setFilteredList] = useState<IFormSubmission[]>(formSubmissions);

    // Refs for clearing filter controls
    const formStatusRef = useRef<EnumSelectDropdownHandle>(null);
    const modifiedByRef = useRef<SearchableStringListDropdownHandle>(null);
    const descriptionRef = useRef<WordSearchInputHandle>(null);
    const dateRangeRef = useRef<DateRangeDropdownHandle>(null);

    // Derive unique "Last Modified By" options from submissions
    const modifiedByOptions = useMemo(() => 
        Array.from(new Set(
            formSubmissions
                .map(s => s.lastModifiedUser)
                .filter(Boolean)
        )).sort((a, b) => a.localeCompare(b))
    , [formSubmissions]);

    // Filter and sort data when filters or source data change
    useEffect(() => {
        if (!formSubmissions) return;

        const hasDateSort = dateFilter?.sort !== undefined;

        const filtered = formSubmissions.filter(submission => {
            // Date filter
            const matchesDate = isIsoDateInRange(submission.submittedDate, dateFilter.start, dateFilter.end);

            // Description filter (case-insensitive contains)
            const matchesDescription = descriptionFilter
                ? submission.formDescription?.toLowerCase().includes(descriptionFilter.toLowerCase())
                : true;

            // Modified by filter
            const matchesModified = modifiedFilter
                ? submission.lastModifiedUser === modifiedFilter
                : true;

            // Status filter
            const matchesStatus = statusFilter
                ? submission.formStatus === statusFilter
                : true;

            return matchesDate && matchesDescription && matchesModified && matchesStatus;
        });

        if (!hasDateSort) {
            setFilteredList(filtered);
            return;
        }

        // Sort by submitted date
        const sorted = [...filtered].sort((a, b) => {
            const dateA = new Date(a.submittedDate).getTime();
            const dateB = new Date(b.submittedDate).getTime();

            const cmp = dateA - dateB;
            return dateFilter.sort === Sort.Ascending ? cmp : -cmp;
        });

        setFilteredList(sorted);

    }, [formSubmissions, dateFilter, descriptionFilter, modifiedFilter, statusFilter]);

    const handleResetFilters = () => {
        formStatusRef.current?.clear();
        modifiedByRef.current?.clear();
        descriptionRef.current?.clear();
        dateRangeRef.current?.clear();

        setDateFilter({ sort: Sort.Ascending });
        setDescriptionFilter('');
        setModifiedFilter(undefined);
        setStatusFilter('');
    };

    const handleDateFilterChange = (range: DateRange) => {
        setDateFilter(range);
    };

    const handleDescriptionFilterChange = (search: WordSearch) => {
        setDescriptionFilter(search.value ?? '');
    };

    const handleModifiedFilterChange = (value: string | undefined) => {
        setModifiedFilter(value);
    };

    const handleStatusFilterChange = (status: FormStatus | undefined) => {
        setStatusFilter(status !== undefined ? FormStatusLabel[status] : '');
    };

    const renderLoading = () => (
        <tr>
            <td colSpan={5}>
                <Delayed waitBeforeShow={750}>
                    <div className="DivPadded DivCenter">
                        <Spinner animation="border" role="status" variant="primary" />
                    </div>
                </Delayed>
            </td>
        </tr>
    );

    const renderNoData = () => (
        <tr>
            <td className='text-muted'>No Form Submissions Found</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
    );

    const ROW_HEIGHT = 40;
    const LIST_HEIGHT = Math.min(Math.max(window.innerHeight * 0.6, 400), 800);

    const Row = ({ index, style }: RowComponentProps) => {
        const submission = filteredList[index];
        return (
            <div 
                style={style} 
                className={`form-submissions-row ${index % 2 === 0 ? 'even' : 'odd'}`}
            >
                <div className="form-submissions-cell" style={{ width: 'var(--fs-col-1)' }}>
                    {new Date(submission.submittedDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    })}
                </div>
                <div className="form-submissions-cell" style={{ width: 'var(--fs-col-2)' }}>
                    {submission.hasTasks ? (
                        <Link
                            to={{
                                pathname: '/dashboardTaskDetails',
                                search: `?entryID=${submission.id}&sort_by=${sortBy}&sort_type=${dateFilter.sort === Sort.Ascending ? 'asc' : 'desc'}` +
                                    `&date=${dateFilter.start ?? ''}&description=${descriptionFilter}&modified=${modifiedFilter ?? ''}&status=${statusFilter}`
                            }}
                        >
                            {submission.formDescription}
                        </Link>
                    ) : (
                        submission.formDescription
                    )}
                </div>
                <div className="form-submissions-cell" style={{ width: 'var(--fs-col-3)' }}>
                    {submission.lastModifiedUser}
                </div>
                <div className="form-submissions-cell" style={{ width: 'var(--fs-col-4)' }}>
                    {submission.formStatus !== "Complete" ? (
                        <button
                            onClick={() => onFormStatusClick(submission.id)}
                            className="fs-status-btn"
                        >
                            {submission.formStatus}
                        </button>
                    ) : (
                        submission.formStatus
                    )}
                </div>
                <div className="form-submissions-cell" style={{ width: 'var(--fs-col-5)' }}></div>
            </div>
        );
    };

    const renderVirtualizedList = () => (
        <List
            style={{ height: LIST_HEIGHT, width: '100%' }}
            rowCount={filteredList.length}
            rowHeight={ROW_HEIGHT}
            className="form-submissions-virtual-list"
            rowComponent={Row}
            rowProps={{}}
        />
    );

    return (
        <div className="border rounded-2 shadow-sm form-submissions">
            {/* Filter Controls Row */}
            <div className="form-submissions-controls border-bottom mb-1 py-2">
                <div>
                    <DateRangeDropdown
                        ref={dateRangeRef}
                        label='Submitted Date'
                        wrapperClassName='mb-0'
                        onChange={handleDateFilterChange}
                        sortable={true}
                        sort={dateFilter.sort}
                    />
                </div>
                <div>
                    <WordSearchInput
                        ref={descriptionRef}
                        label='Form Description'
                        wrapperClassName='mb-0'
                        placeholder="Filter description..."
                        onChange={handleDescriptionFilterChange}
                    />
                </div>
                <div>
                    <SearchableStringListDropdown
                        ref={modifiedByRef}
                        label='Last Modified By'
                        options={modifiedByOptions}
                        wrapperClassName='mb-0'
                        placeholder="Modified by..."
                        onChange={handleModifiedFilterChange}
                    />
                </div>
                <div>
                    <EnumSelectDropdown
                        ref={formStatusRef}
                        label='Form Status'
                        wrapperClassName='mb-0'
                        enumObj={FormStatus}
                        clearable={true}
                        onChange={handleStatusFilterChange}
                    />
                </div>
                <div>
                    <button
                        title='Reset all filters'
                        className='form-submissions-reset-btn'
                        onClick={handleResetFilters}
                    >
                        <ResetFilter />
                    </button>
                </div>
            </div>

            {/* Scrollable Table Container */}
            <div className="form-submissions-table-container">
                {isLoading ? (
                    <table className="form-submissions-table">
                        <colgroup>
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                        </colgroup>
                        <tbody>
                            {renderLoading()}
                        </tbody>
                    </table>
                ) : filteredList.length > 0 ? (
                    renderVirtualizedList()
                ) : (
                    <table className="form-submissions-table">
                        <colgroup>
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                        </colgroup>
                        <tbody>
                            {renderNoData()}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
